import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, STATUS_LABELS } from "@/components/badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Inbox, PlusCircle } from "lucide-react";

function TrendBadge({ value, testid }) {
  if (!value || value <= 0) return null;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
      <TrendingUp className="h-3 w-3" /> +{value} cette semaine
    </span>
  );
}

function Kpi({ label, value, testid, accent, trend, trendTestid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-border bg-card p-5">
      <div className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className={`font-heading text-3xl font-semibold ${accent ? "text-primary" : ""}`}>{value ?? "—"}</span>
      </div>
      {trend !== undefined && <div className="mt-2 h-5"><TrendBadge value={trend} testid={trendTestid} /></div>}
    </div>
  );
}

function TicketsTrendChart({ data }) {
  const total = (data || []).reduce((s, d) => s + d.count, 0);
  return (
    <div className="mt-8 rounded-sm border border-border bg-card p-5" data-testid="tickets-trend-chart">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium">Tickets créés — 7 derniers jours</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{total} ticket{total > 1 ? "s" : ""} créé{total > 1 ? "s" : ""} sur la période</p>
        </div>
      </div>
      {total === 0 ? (
        <div className="flex h-[200px] flex-col items-center justify-center text-center" data-testid="trend-chart-empty">
          <Inbox className="h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">Aucun ticket créé ces 7 derniers jours.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 13 }}
              labelStyle={{ color: "hsl(var(--foreground))" }}
              formatter={(v) => [`${v} ticket${v > 1 ? "s" : ""}`, "Créés"]} />
            <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2.5}
              dot={{ r: 3, fill: "hsl(var(--primary))" }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function EmptyDashboard({ canCreate, variant = "company" }) {
  const isPersonal = variant === "personal";
  return (
    <div className="mt-6 rounded-sm border border-dashed border-border bg-card p-12 text-center" data-testid="dashboard-empty-state">
      <Inbox className="mx-auto h-12 w-12 text-muted-foreground/40" />
      <h2 className="mt-6 font-heading text-xl font-semibold">
        {isPersonal ? "Aucun ticket ne vous est assigné" : "Aucune donnée pour le moment"}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {isPersonal
          ? "Dès qu'un ticket vous sera assigné, vos statistiques et votre activité s'afficheront ici."
          : "Les statistiques et graphiques s'afficheront ici dès que les premiers tickets seront créés et traités pour cette société."}
      </p>
      {canCreate && (
        <Link to="/app/tickets" data-testid="dashboard-empty-cta"
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700">
          <PlusCircle className="h-4 w-4" /> Créer un premier ticket
        </Link>
      )}
    </div>
  );
}

function PeriodEmptyNote() {
  return (
    <div className="mt-8 rounded-sm border border-dashed border-border bg-card p-8 text-center" data-testid="stats-empty">
      <p className="text-sm font-medium">Aucune donnée sur la période sélectionnée</p>
      <p className="mt-1 text-xs text-muted-foreground">Essayez une autre période ; le graphique ci-dessus couvre toujours les 7 derniers jours.</p>
    </div>
  );
}



function fmtDur(min) {
  if (min === null || min === undefined) return "Non disponible";
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  if (h < 24) return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
  const j = Math.floor(h / 24), hh = h % 24;
  return hh ? `${j} j ${hh} h` : `${j} j`;
}

const PERIODS = [
  ["today", "Aujourd'hui"], ["7d", "7 derniers jours"], ["30d", "30 derniers jours"],
  ["month", "Mois en cours"], ["custom", "Période personnalisée"],
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [stale, setStale] = useState([]);
  const [cs, setCs] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("all");

  const isSuper = user?.role === "super_admin";
  const isCollab = user?.role === "collaborator";

  useEffect(() => {
    api.get("/stats/dashboard").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/tickets/rappels").then(({ data }) => setStale(data)).catch(() => {});
    if (isSuper) api.get("/companies").then(({ data }) => setCompanies(data.filter((c) => !c.deleted_at))).catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (isSuper && companyId !== "all") params.set("company_id", companyId);
    api.get(`/tickets?${params}`).then(({ data }) => setRecent(data.slice(0, 6))).catch(() => {});
  }, [companyId, isSuper]);

  useEffect(() => {
    if (period === "custom" && (!customFrom || !customTo)) return;
    const params = new URLSearchParams({ period });
    if (period === "custom") { params.set("date_from", customFrom); params.set("date_to", customTo); }
    if (isSuper && companyId !== "all") params.set("company_id", companyId);
    setCs(null);
    api.get(`/dashboard/company-stats?${params}`).then(({ data }) => setCs(data)).catch(() => setCs(null));
  }, [period, customFrom, customTo, companyId, isSuper]);

  const t = cs?.totals;
  const hasData = (t?.total ?? 0) > 0;
  const hasAnyData = cs?.has_any_data;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Bonjour {user?.first_name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isSuper ? "Supervision globale Suivio360" : user?.company?.name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isSuper && (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-52 rounded-sm" data-testid="company-stats-selector">
                <SelectValue placeholder="Toutes les sociétés" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sociétés</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-48 rounded-sm" data-testid="dashboard-period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {period === "custom" && (
            <>
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="w-40 rounded-sm" data-testid="dashboard-custom-from" />
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="w-40 rounded-sm" data-testid="dashboard-custom-to" />
            </>
          )}
        </div>
      </div>

      {isCollab ? (
        cs && !hasAnyData ? (
          <EmptyDashboard canCreate={false} variant="personal" />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="collab-personal-stats">
              <Kpi label="Mes tickets assignés" value={cs?.personal?.assignes} testid="kpi-my-assigned" accent
                trend={cs?.trends?.created_7d} trendTestid="kpi-my-assigned-trend" />
              <Kpi label="Mes tickets en cours" value={cs?.personal?.en_cours} testid="kpi-my-in-progress" />
              <Kpi label="Mes tickets clôturés" value={cs?.personal?.clotures} testid="kpi-my-closed"
                trend={cs?.trends?.closed_7d} trendTestid="kpi-my-closed-trend" />
              <Kpi label="Mes tickets en retard" value={cs?.personal?.en_retard} testid="kpi-my-late" accent={(cs?.personal?.en_retard ?? 0) > 0} />
              <Kpi label="Ma 1ʳᵉ réponse moyenne" value={cs ? fmtDur(cs?.personal?.premiere_reponse_min) : "—"} testid="kpi-my-first-response" />
              <Kpi label="Ma clôture moyenne" value={cs ? fmtDur(cs?.personal?.cloture_min) : "—"} testid="kpi-my-close-time" />
            </div>
            {cs && <TicketsTrendChart data={cs.timeseries_7d || []} />}
          </>
        )
      ) : cs && !hasAnyData ? (
        <EmptyDashboard canCreate={true} variant="company" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <Kpi label="Tickets totaux" value={t?.total} testid="kpi-total"
              trend={cs?.trends?.created_7d} trendTestid="kpi-total-trend" />
            <Kpi label="Tickets ouverts" value={t?.ouverts} testid="kpi-tickets-open" accent />
            <Kpi label="Tickets en cours" value={t?.en_cours} testid="kpi-in-progress" />
            <Kpi label="Tickets clôturés" value={t?.clotures} testid="kpi-tickets-closed"
              trend={cs?.trends?.closed_7d} trendTestid="kpi-tickets-closed-trend" />
            <Kpi label="Tickets urgents" value={t?.urgents} testid="kpi-tickets-urgent" accent={(t?.urgents ?? 0) > 0} />
            <Kpi label="Tickets en retard" value={t?.en_retard} testid="kpi-late" accent={(t?.en_retard ?? 0) > 0} />
          </div>

          {cs && <TicketsTrendChart data={cs.timeseries_7d || []} />}

          {cs && !hasData && <PeriodEmptyNote />}

          {cs && hasData && (
            <>
              <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="stats-delays">
                <div className="rounded-sm border border-border bg-card p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Temps moyen de première réponse</div>
                  <div className="mt-3 font-heading text-2xl font-semibold" data-testid="avg-first-response">{fmtDur(cs.delais?.premiere_reponse_min)}</div>
                  <p className="mt-1 text-xs text-muted-foreground">Création du ticket → première réponse de l'équipe</p>
                </div>
                <div className="rounded-sm border border-border bg-card p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Temps moyen de clôture</div>
                  <div className="mt-3 font-heading text-2xl font-semibold" data-testid="avg-close-time">{fmtDur(cs.delais?.cloture_min)}</div>
                  <p className="mt-1 text-xs text-muted-foreground">Création du ticket → clôture</p>
                </div>
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <div data-testid="stats-by-status">
                  <h2 className="mb-4 text-lg font-medium">Répartition par statut</h2>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(cs.by_status || {}).map(([s, n]) => (
                      <span key={s} data-testid={`status-count-${s}`}
                        className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-2 text-sm">
                        {STATUS_LABELS[s] || s} <b className="font-heading">{n}</b>
                      </span>
                    ))}
                  </div>
                </div>
                <div data-testid="stats-by-category">
                  <h2 className="mb-4 text-lg font-medium">Tickets par catégorie</h2>
                  <div className="space-y-2 rounded-sm border border-border bg-card p-4">
                    {(cs.by_category || []).map((c) => (
                      <div key={c.name} data-testid={`category-stat-${c.name}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                          <span className="ml-3 text-xs text-muted-foreground">{c.count} · {c.pct}%</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-sm bg-accent">
                          <div className="h-1.5 rounded-sm bg-primary" style={{ width: `${c.pct}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {(cs.by_collaborator || []).length > 0 && (
                <div className="mt-8" data-testid="stats-by-collaborator">
                  <h2 className="text-lg font-medium">Activité par collaborateur</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Ces indicateurs permettent de suivre la charge de travail et d'améliorer l'organisation du traitement des demandes.
                  </p>
                  <div className="mt-4 overflow-x-auto rounded-sm border border-border bg-card">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="px-4 py-3 font-semibold">Collaborateur</th>
                          <th className="px-4 py-3 font-semibold">Assignés</th>
                          <th className="px-4 py-3 font-semibold">Clôturés</th>
                          <th className="px-4 py-3 font-semibold">En retard</th>
                          <th className="px-4 py-3 font-semibold">1ʳᵉ réponse moy.</th>
                          <th className="px-4 py-3 font-semibold">Clôture moy.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cs.by_collaborator.map((c) => (
                          <tr key={c.user_id} className="border-b border-border last:border-b-0" data-testid={`collab-stat-${c.user_id}`}>
                            <td className="px-4 py-3 font-medium">{c.name}</td>
                            <td className="px-4 py-3">{c.assignes}</td>
                            <td className="px-4 py-3">{c.clotures}</td>
                            <td className={`px-4 py-3 ${c.en_retard > 0 ? "font-semibold text-primary" : ""}`}>{c.en_retard}</td>
                            <td className="px-4 py-3">{fmtDur(c.premiere_reponse_min)}</td>
                            <td className="px-4 py-3">{fmtDur(c.cloture_min)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!isSuper && stale.length > 0 && (
        <div className="mt-10" data-testid="dashboard-stale-section">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-primary">À traiter en priorité</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">Tickets assignés sans réponse de l'équipe depuis plus de 48 h.</p>
          </div>
          <div className="rounded-sm border border-primary/30 bg-card">
            {stale.map((t) => (
              <Link key={t.id} to={`/app/tickets/${t.id}`} data-testid={`dashboard-stale-${t.number}`}
                className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4 last:border-b-0 hover:bg-accent transition-colors duration-200">
                <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                <span className="min-w-0 flex-1 basis-full break-words text-sm font-medium sm:basis-auto sm:truncate">{t.subject}</span>
                <span className="text-xs text-muted-foreground">assigné le {fmtDateTime(t.assigned_at)}</span>
                <StatusBadge status={t.status} />
                <PriorityBadge priority={t.priority} />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Dernières demandes</h2>
          <Link to="/app/tickets" data-testid="dashboard-view-all-tickets" className="text-sm text-primary hover:underline">Tout voir</Link>
        </div>
        <div className="rounded-sm border border-border bg-card">
          {recent.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Aucun ticket pour le moment.</div>}
          {recent.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Link to={`/app/tickets/${t.id}`} data-testid={`dashboard-ticket-${t.number}`}
                className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4 last:border-b-0 hover:bg-accent transition-colors duration-200">
                <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                <span className="min-w-0 flex-1 basis-full break-words text-sm font-medium sm:basis-auto sm:truncate">{t.subject}</span>
                {isSuper && <span className="text-xs text-muted-foreground">{t.company_name}</span>}
                <span className="text-xs text-muted-foreground">{t.client_name}</span>
                <StatusBadge status={t.status} />
                <PriorityBadge priority={t.priority} />
                <span className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
